package com.example.hukakmp.data

import com.example.hukakmp.model.AchievementCatalog
import com.example.hukakmp.model.AchievementState
import com.example.hukakmp.model.AchievementUnlockDocument
import com.example.hukakmp.model.PartePesca
import com.example.hukakmp.util.parsearFecha
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.firestore.firestore

class AchievementsRepository {
    private val firestore = Firebase.firestore

    suspend fun sincronizar(partes: List<PartePesca>): List<AchievementState> {
        val userId = Firebase.auth.currentUser?.uid ?: return AchievementCatalog.all.map {
            AchievementState(definition = it, isUnlocked = false)
        }

        val desbloqueados = obtenerDesbloqueados(userId).toMutableMap()
        val cumplidos = evaluarLogros(partes)

        cumplidos.forEach { achievementId ->
            if (achievementId !in desbloqueados) {
                val timestamp = kotlin.time.Clock.System.now().toEpochMilliseconds()
                try {
                    firestore.document("users/$userId/unlocked_achievements/$achievementId")
                        .set(AchievementUnlockDocument(timestamp = timestamp), merge = true)
                    desbloqueados[achievementId] = timestamp
                } catch (_: Exception) {
                    // La UI sigue mostrando el logro calculado aunque la red falle.
                    desbloqueados[achievementId] = timestamp
                }
            }
        }

        return AchievementCatalog.all.map { definition ->
            AchievementState(
                definition = definition,
                isUnlocked = definition.id in desbloqueados,
                unlockedAtMillis = desbloqueados[definition.id],
            )
        }
    }

    private suspend fun obtenerDesbloqueados(userId: String): Map<String, Long> {
        return try {
            val snapshot = firestore.collection("users/$userId/unlocked_achievements").get()
            snapshot.documents.associate { document ->
                val timestamp = try {
                    document.data<AchievementUnlockDocument>().timestamp
                } catch (_: Exception) {
                    0L
                }
                document.id to timestamp
            }
        } catch (_: Exception) {
            emptyMap()
        }
    }

    private fun evaluarLogros(partes: List<PartePesca>): Set<String> {
        if (partes.isEmpty()) return emptySet()

        val ids = mutableSetOf<String>()
        ids += "mi_primer_parte"

        partes.forEach { parte ->
            val totalPeces = if (parte.cantidadTotal > 0) {
                parte.cantidadTotal
            } else {
                parte.peces.sumOf { it.cantidad }
            }

            when {
                totalPeces == 0 -> ids += "zapatero_wade"
                totalPeces == 1 -> ids += "solo_un_pez"
                totalPeces >= 10 -> ids += "pesca_abundante"
            }

            parte.fecha?.let(::parsearFecha)?.let { fecha ->
                val dia = fecha.dayOfMonth
                val mes = fecha.monthNumber
                if (mes == 12 && dia in 24..31) ids += "pescador_navideño"
                if (mes == 1 && dia in 1..7) ids += "pescador_año_nuevo"
                if (mes == 1 && dia == 6) ids += "regalo_de_reyes"
                if (mes in 6..8) ids += "pescador_invernal"
                if (mes in 9..11) ids += "pescador_primaveral"
            }

            parte.horaInicio
                ?.substringBefore(":")
                ?.toIntOrNull()
                ?.let { hora ->
                    when (hora) {
                        in 5..7 -> ids += "madrugador"
                        in 19..23 -> ids += "pescador_nocturno"
                        in 0..4 -> ids += "noctambulo"
                    }
                }

            val especiesDistintas = parte.peces
                .map { normalizarEspecie(it.especie) }
                .filter { it.isNotBlank() }
                .distinct()
            if (especiesDistintas.size >= 5) ids += "variedad_es_vida"

            parte.peces.forEach { captura ->
                val nombre = normalizarEspecie(captura.especie)
                when {
                    "dorado" in nombre -> {
                        ids += "cazador_de_dorados"
                        if (captura.cantidad >= 5) ids += "rey_del_rio"
                    }
                    "surubi" in nombre -> ids += "amigo_del_surubi"
                    "pejerrey" in nombre && captura.cantidad >= 10 -> ids += "pejerreyes_master"
                }
            }

            val ubicacion = parte.ubicacion
            if (ubicacion?.latitud != null && ubicacion.longitud != null) {
                ids += "explorador"
            }
            if (parte.fotos.isNotEmpty()) ids += "primera_foto"
        }

        return ids
    }

    private fun normalizarEspecie(valor: String): String = valor
        .trim()
        .lowercase()
        .replace("á", "a")
        .replace("é", "e")
        .replace("í", "i")
        .replace("ó", "o")
        .replace("ú", "u")
        .replace("ü", "u")
}
